import React, { useState } from 'react';
import { posterPlaceholder } from '../utils/posterPlaceholder';

/**
 * The poster slot — a fixed-size box that never lets the image dictate layout.
 * docs/plans/2026-08-events-list-view/README (design handoff, Aug 31 2026)
 *
 * Posters arrive in every aspect ratio (16:9 banners, 3:4 and 4:5 flyers,
 * squares). A single fixed rectangle with `object-fit: cover` either crops them
 * or makes card heights jump. The fix is two copies of the same image inside a
 * fixed slot:
 *
 *   1. an ambient fill — `object-fit: cover`, blurred and saturated, inset -12%
 *      and sized 124% so the blur bleeds past the edges and no soft edge shows
 *      against the 2px border;
 *   2. the real poster on top — `object-fit: contain` at width/height 100% (NOT
 *      max-width/max-height) so a landscape poster scales UP to fill the slot
 *      instead of sitting small in the middle.
 *
 * Nothing is ever cropped, no slot is ever empty, heights are fixed regardless
 * of poster shape. The blur is the one deliberate departure from the design
 * system's no-blur rule and is used ONLY here — never in the chrome.
 *
 * Both copies point at the same URL so the browser decodes it once.
 *
 * `referrerPolicy="no-referrer"` is load-bearing for scraped posters: some venue sites
 * hotlink-protect their uploads and return 403 when the Referer is a foreign origin
 * (redbirdbrewing.com does), which tripped onError and dropped the card to its colour
 * placeholder even though the stored URL was perfectly good.
 */
export default function PosterSlot({
  posterUrl,
  alt = '',
  /** Ambient-fill blur radius in px — 30 grid, 22 mobile list, 20 desktop row. */
  blur = 30,
  /** Slot padding as Tailwind classes — p-3 grid, p-2.5 mobile, p-2 desktop row. */
  padClass = 'p-3',
  /** Stable key for the deterministic colour block when there's no poster. */
  fallbackKey,
  fallbackFill,
  className = '',
  style,
}) {
  const [failed, setFailed] = useState(false);
  const show = !!posterUrl && !failed;
  const fill = fallbackFill || posterPlaceholder(fallbackKey);

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{ background: show ? '#000' : fill, ...style }}
    >
      {show && (
        <>
          <img
            src={posterUrl}
            alt=""
            aria-hidden="true"
            loading="lazy"
            referrerPolicy="no-referrer"
            className="absolute block object-cover"
            style={{
              inset: '-12%',
              width: '124%',
              height: '124%',
              filter: `blur(${blur}px) saturate(1.5)`,
            }}
          />
          <span className={`absolute inset-0 block ${padClass}`}>
            <img
              src={posterUrl}
              alt={alt}
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={() => setFailed(true)}
              className="block h-full w-full object-contain"
            />
          </span>
        </>
      )}
    </div>
  );
}
